import { Txn } from '../money.model';
import { spendableTxns, recentMonths } from '../dashboard/dashboard.aggregate';
import { median } from '../rules/recurring.detector';

/** One tappable pre-fill for the monthly contribution field. */
export interface SavingsChip { label: string; monthlyCents: number; }

/** Horizons the milestone table reports, when the chosen horizon reaches them. */
export const MILESTONE_YEARS = [5, 10, 20, 30];

const CHIP_WINDOW_MONTHS = 12;
const CHIP_RECENT_MONTHS = 6;
const MONTHS_PER_YEAR = 12;
const BASIS_POINTS_PER_UNIT = 10000;

/**
 * Whole months to reach a target, or null when nothing is being contributed.
 *
 * Deliberately linear — no interest. A savings goal on this horizon is a bank
 * balance, not an investment, and compounding it would report a date earlier
 * than the money actually arrives.
 */
export function monthsToGoal(targetCents: number, monthlyCents: number): number | null {
  if (!(monthlyCents > 0)) return null;
  if (targetCents <= 0) return 0;
  return Math.ceil(targetCents / monthlyCents);
}

/**
 * `fromISO` shifted by whole months, clamped to the last day of the target
 * month.
 *
 * Local calendar arithmetic throughout — never toISOString(). There are two
 * opposite date hazards in this codebase and it is worth naming both, because
 * they are easy to conflate and a comment stating the wrong one has already
 * been written once:
 *
 *   1. Reading a local-time Date back through toISOString(), as here. That
 *      reports UTC, and EAST of Greenwich (a positive offset) local midnight
 *      is already the previous UTC day — so the round-trip loses a day. Same
 *      reasoning as addDays in rules/recurring.detector.ts.
 *   2. Rendering a bare ISO date string, which is parsed as UTC midnight and
 *      so displays a day early WEST of Greenwich. That one is why every
 *      template here pipes `x + 'T00:00:00'`.
 *
 * Using the Date object's own local getters and setters throughout avoids the
 * first at every offset; this function never produces a bare string for a
 * template to reparse, so callers still apply the 'T00:00:00' guard.
 *
 * The clamp matters: setMonth alone turns 31 January plus one month into
 * 3 March, so a goal would report a date in the wrong month entirely.
 */
export function targetDate(fromISO: string, months: number): string {
  const d = new Date(`${fromISO}T00:00:00`);
  const day = d.getDate();
  d.setDate(1);                            // shift months without overflowing
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Balance at the end of each year, year 0 (today) through `years`.
 *
 * Compounding is monthly; only the reported points are annual. A per-month
 * series would be 360 points on a 240-unit viewBox at a 30-year horizon —
 * illegible, and pointless at that timescale. Annual resolution is also what
 * milestoneRows reads, so the chart and the table can never disagree.
 */
export function projectBalance(
  startCents: number, monthlyCents: number, rateBasisPoints: number, years: number,
): { year: number; cents: number }[] {
  const monthlyRate = rateBasisPoints / BASIS_POINTS_PER_UNIT / MONTHS_PER_YEAR;

  return Array.from({ length: Math.max(years, 0) + 1 }, (_, year) => {
    const n = year * MONTHS_PER_YEAR;
    // The annuity term divides by the rate, so a zero rate takes the linear
    // form rather than producing NaN.
    const cents = monthlyRate === 0
      ? startCents + monthlyCents * n
      : startCents * Math.pow(1 + monthlyRate, n)
        + monthlyCents * ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate);
    return { year, cents: Math.round(cents) };
  });
}

/** Milestone figures, read straight off the same series the chart plots. */
export function milestoneRows(
  startCents: number, monthlyCents: number, rateBasisPoints: number, years: number,
): { years: number; cents: number }[] {
  const byYear = new Map(
    projectBalance(startCents, monthlyCents, rateBasisPoints, years)
      .map(p => [p.year, p.cents]));

  return MILESTONE_YEARS
    .filter(y => byYear.has(y))
    .map(y => ({ years: y, cents: byYear.get(y)! }));
}

/**
 * Pre-fill figures for the monthly contribution field, from the user's own
 * history: the twelve-month average, the twelve-month median, and the median
 * of the trailing six months.
 *
 * Three figures rather than one because the real ledger does not support one.
 * Monthly net ranges from -$861 to +$7,219 across a single year, so the
 * average is pulled upward by one exceptional month while the recent median
 * reflects a genuine rise in spending. Showing all three, labelled, puts that
 * disagreement in front of the user rather than hiding it behind a choice
 * made here.
 *
 * Whole months only: the current month is still accruing, so including it
 * would drag every figure down by an amount that shrinks as the month goes on.
 *
 * The windows are calendar windows, not "the last N months with data" —
 * `recentMonths` off the last completed month, so an unimported month or one
 * with only transfers is missing data rather than silently shrinking the
 * window to fewer than N calendar months. A month with no data contributes
 * nothing to the average or median; it is not counted as a net of zero,
 * which would understate every figure with a false "saved nothing".
 */
export function savingsRateChips(txns: Txn[], asOf: string): SavingsChip[] {
  const currentMonth = asOf.slice(0, 7);
  const [lastCompleteMonth] = recentMonths(currentMonth, 2);

  const netByMonth = new Map<string, number>();
  spendableTxns(txns ?? []).forEach(t => {
    const month = t.date.slice(0, 7);
    if (month >= currentMonth) return;
    netByMonth.set(month, (netByMonth.get(month) ?? 0) + t.amount);
  });

  if (!netByMonth.size) return [];

  const netsInWindow = (months: string[]) =>
    months.filter(m => netByMonth.has(m)).map(m => netByMonth.get(m)!);

  const window12 = netsInWindow(recentMonths(lastCompleteMonth, CHIP_WINDOW_MONTHS));
  const window6 = netsInWindow(recentMonths(lastCompleteMonth, CHIP_RECENT_MONTHS));
  if (!window12.length) return [];

  const chips: SavingsChip[] = [
    { label: 'avg', monthlyCents: Math.round(window12.reduce((a, b) => a + b, 0) / window12.length) },
    { label: 'median', monthlyCents: median(window12) },
  ];

  // Only worth showing separately when it actually covers fewer months of
  // data than the 12-month figures above — otherwise it is the same median
  // wearing a different label.
  if (window6.length && window6.length < window12.length) {
    chips.push({ label: 'last 6mo', monthlyCents: median(window6) });
  }

  return chips;
}
